import { useCallback, useMemo } from "react";
import { api } from "../api/client.js";
import { useResource } from "./useResource.js";
import { FLEET_KEYS } from "../lib/resourceKeys.js";

const EMPTY = [];

const byName = (a, b) => a.name.localeCompare(b.name);

/**
 * useGroups — los grupos de vehículos, compartidos entre pantallas.
 *
 * Antes cada pantalla de Flota montaba su propia copia de este hook y cada
 * copia pedía `/api/groups` al montar. Vehículos, Dispositivos y Conductores
 * gastaban una ida y vuelta cada una para traerse EXACTAMENTE la misma lista,
 * y volver a una pantalla ya visitada la pedía otra vez.
 *
 * Ahora todas leen la misma entrada de `resourceCache`: la primera paga la
 * petición, las demás pintan en el acto, y las escrituras (crear, renombrar,
 * borrar, mover miembros) actualizan esa entrada única — así que un grupo
 * creado en la pantalla de Grupos ya está en el filtro de Vehículos sin
 * recargar nada.
 */
export function useGroups() {
  const { data, loading, error, refresh, setData } = useResource(
    FLEET_KEYS.groups,
    api.getGroups,
    { fallback: EMPTY },
  );

  const groups = Array.isArray(data) ? data : EMPTY;

  const createGroup = useCallback(
    async (body) => {
      const newGroup = await api.createGroup(body);
      if (!newGroup || typeof newGroup !== "object")
        throw new Error("Invalid response from server");
      setData((prev) => [...(prev ?? []), newGroup].sort(byName));
      return newGroup;
    },
    [setData],
  );

  const updateGroup = useCallback(
    async (id, body) => {
      const updated = await api.updateGroup(id, body);
      setData((prev) =>
        (prev ?? []).map((g) => (g.id === id ? { ...g, ...updated } : g)).sort(byName),
      );
      return updated;
    },
    [setData],
  );

  const deleteGroup = useCallback(
    async (id) => {
      await api.deleteGroup(id);
      setData((prev) => (prev ?? []).filter((g) => g.id !== id));
    },
    [setData],
  );

  const updateMembers = useCallback(
    async (id, imeis) => {
      const result = await api.updateGroupMembers(id, imeis);
      setData((prev) =>
        (prev ?? []).map((g) =>
          g.id === id
            ? { ...g, member_imeis: imeis, member_count: imeis.length }
            : g,
        ),
      );
      return result;
    },
    [setData],
  );

  // Identidad estable del objeto retornado: consumidores memoizados dependen
  // de que no cambie de referencia en cada render del padre.
  return useMemo(
    () => ({
      groups,
      loading,
      error: error ? (error.message ?? String(error)) : null,
      loadGroups: refresh,
      createGroup,
      updateGroup,
      deleteGroup,
      updateMembers,
    }),
    [
      groups,
      loading,
      error,
      refresh,
      createGroup,
      updateGroup,
      deleteGroup,
      updateMembers,
    ],
  );
}
